/*
 * Shared by Broadcast and Paper: publish promotion configuration, not theme identity.
 * Load synchronously in <head>, before theme/app cart clients capture window.fetch.
 * This sends metadata only. The Shopify Function must separately consume the mode.
 */
(() => {
  'use strict';

  const configElement = document.getElementById('tc-offer-mode-config');
  if (!configElement || window.jaq?.offerMode) return;

  const config = JSON.parse(configElement.textContent);
  const allowedModes = new Set(['none', 'product_linked', 'spend_threshold']);
  const attribute = 'tc_offer_mode';
  const rawFetch = window.fetch.bind(window);
  const root = new URL(config.cartRoot, window.location.origin);
  // Liquid routes.root_url may be '/de', while Ajax needs a trailing slash.
  root.pathname = `${root.pathname.replace(/\/$/, '')}/`;
  const deadlineMs = 6000;
  let status = 'pending';
  let error = null;
  let pending = null;
  let interactionBlocked = false;
  const queuedForms = new WeakSet();
  const queuedControls = new WeakSet();

  // The deadline includes response-body consumption, not just HTTP headers.
  async function readCart(endpoint, options = {}) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), deadlineMs);
    try {
      const response = await rawFetch(new URL(endpoint, root).href, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Offer mode: HTTP ${response.status}`);
      return await response.json();
    } finally {
      window.clearTimeout(timer);
    }
  }

  function matches(cart) {
    return cart?.attributes?.[attribute] === config.mode;
  }

  function publish() {
    document.documentElement.dataset.tcOfferModeStatus = status;
    const notice = document.querySelector('[data-tc-offer-mode-notice]');
    if (notice) {
      notice.hidden =
        status !== 'error' && !(status === 'pending' && interactionBlocked);
      notice.querySelector('[data-tc-offer-mode-message]').textContent =
        status === 'error' ? config.strings.error : config.strings.pending;
      notice.querySelector('[data-tc-offer-mode-retry]').hidden =
        status !== 'error';
      notice.querySelector('[data-tc-offer-mode-retry]').disabled =
        status === 'pending';
    }
    window.dispatchEvent(
      new CustomEvent(`tc:offer-mode:${status}`, {
        detail: { mode: config.mode, error },
      })
    );
  }

  async function synchronize() {
    if (!allowedModes.has(config.mode)) {
      throw new Error('Offer mode: unsupported theme setting');
    }
    if (
      root.origin !== window.location.origin ||
      !root.pathname.endsWith('/')
    ) {
      throw new Error('Offer mode: invalid cart route');
    }

    // Always inspect the current cart, not a localStorage flag or cached Liquid value.
    // A matching mode costs one read, but no attribute POST or cart re-render.
    const cart = await readCart('cart.js');
    if (matches(cart)) return cart;

    try {
      const updated = await readCart('cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Do not send updates, notes, gifts, or unrelated attributes.
        body: JSON.stringify({ attributes: { [attribute]: config.mode } }),
      });
      if (!matches(updated))
        throw new Error('Offer mode: attribute was not confirmed');
      return updated;
    } catch (writeError) {
      // A timed-out POST might still have committed. Read before suggesting a retry;
      // never assume aborting the browser request undoes a Shopify cart mutation.
      const recovered = await readCart('cart.js');
      if (matches(recovered)) return recovered;
      throw writeError;
    }
  }

  function ensure() {
    if (pending) return pending;
    status = 'pending';
    error = null;
    publish();
    pending = synchronize().then(
      (cart) => {
        status = 'ready';
        publish();
        return cart;
      },
      (failure) => {
        status = 'error';
        error = failure;
        publish();
        throw failure;
      }
    );
    // Initialization is also consumed by gated fetches/the app. This handler prevents
    // an unhandled rejection if the customer has not interacted with the cart yet.
    void pending.catch(() => {});
    return pending;
  }

  function retry() {
    if (status === 'pending') return ensure();
    pending = null;
    return ensure();
  }

  window.jaq = window.jaq || {};
  window.jaq.offerMode = Object.freeze({
    mode: config.mode,
    get ready() {
      return ensure();
    },
    get status() {
      return status;
    },
    get error() {
      return error;
    },
    ensure,
    retry,
  });

  function cartRequest(input, options) {
    const value =
      typeof input === 'string' || input instanceof URL ? input : input?.url;
    if (!value) return false;
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    const method = String(
      options?.method || input?.method || 'GET'
    ).toUpperCase();
    // Both themes have some unlocalized cart calls. Recognise those as well as
    // Shopify's locale-aware routes. Do not delay unrelated product/API requests.
    return (
      (method === 'GET' && /\/cart(?:\.js)?\/?$/.test(url.pathname)) ||
      (method !== 'GET' &&
        /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname))
    );
  }

  window.fetch = async (input, options) => {
    if (cartRequest(input, options)) await ensure();
    // Keep the original request, body, headers and abort signal intact.
    return rawFetch(input, options);
  };

  const checkoutSelector =
    '[name="checkout"], .shopify-payment-button, .additional-checkout-buttons, shopify-accelerated-checkout, shopify-accelerated-checkout-cart';

  // A native cart form or checkout link does not pass through window.fetch.
  // Defer the original interaction until confirmed; do not change theme-owned
  // disabled states, terms validation, or stock validation on checkout buttons.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || status === 'ready') return;
      const url = new URL(form.action, window.location.origin);
      if (
        url.origin !== window.location.origin ||
        !/\/(?:cart(?:\/(?:add|change|update|clear))?|checkout)(?:\.js)?\/?$/.test(
          url.pathname
        )
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      interactionBlocked = true;
      publish();
      if (queuedForms.has(form)) return;
      queuedForms.add(form);
      const submitter = event.submitter;
      void ensure()
        .then(() => {
          if (!form.isConnected) return;
          if (submitter?.isConnected) form.requestSubmit(submitter);
          else form.requestSubmit();
        })
        .catch(() => {})
        .finally(() => queuedForms.delete(form));
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-tc-offer-mode-retry]')) {
        void retry().catch(() => {});
        return;
      }
      if (status === 'ready') return;
      const link = target?.closest('a[href]');
      const url = link && new URL(link.href, window.location.origin);
      const isCheckoutLink =
        url?.origin === window.location.origin &&
        /^\/checkout(?:\/|$)/.test(
          url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '')
        );
      const control = target?.closest(checkoutSelector);
      if (!isCheckoutLink && !control) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      interactionBlocked = true;
      publish();
      if (control?.matches('[name="checkout"]')) {
        if (queuedControls.has(control)) return;
        queuedControls.add(control);
        void ensure()
          .then(() => {
            if (control.isConnected && !control.disabled) control.click();
          })
          .catch(() => {})
          .finally(() => queuedControls.delete(control));
        return;
      }
      // Preserve modified/new-tab clicks by asking the customer to retry after readiness.
      // Never synthesize clicks into accelerated checkout's closed shadow DOM.
      if (
        !isCheckoutLink ||
        (link.target && link.target !== '_self') ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      void ensure()
        .then(() => {
          if (link.isConnected) window.location.assign(link.href);
        })
        .catch(() => {});
    },
    true
  );

  document.addEventListener('DOMContentLoaded', publish, { once: true });
  // Back/forward cache can revive a page after another theme has changed its cart.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) void retry().catch(() => {});
  });
  void ensure();
})();
